// src/services/tenantConfigAPI.ts
// FIXED: Removed getUserCertifications method + Centralized Logging + Environment Config

import { authService } from './authService';

// 📊 Centralized logging
import { logger } from '../utils/logger';

// 🔧 Environment configuration
import { getApiBaseUrl } from './configService';

// Create service-specific logger
const tenantConfigLogger = logger.createServiceLogger('TenantConfigAPI');

export interface TenantConfiguration {
  tenantId: string;
  configurationKey: string;
  displayName: string;
  description: string;
  values: string[];
  isSystemDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
}

export interface CreateConfigurationRequest {
  configurationKey: string;
  displayName: string;
  description?: string;
  values: string[];
}

export interface UpdateConfigurationRequest {
  displayName?: string;
  description?: string;
  values: string[];
}

export interface TenantConfigAPIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

export interface GetAllConfigurationsResponse {
  configurations: TenantConfiguration[];
  count: number;
  initialized?: boolean;
  message?: string;
}

export interface GetConfigurationResponse {
  configuration?: TenantConfiguration;
  data?: {
    configuration: TenantConfiguration;
  };
  initialized?: boolean;
}

class TenantConfigAPI {
  private baseURL: string;

  constructor() {
    // ✅ FIXED: Use environment configuration instead of hardcoded URL
    this.baseURL = getApiBaseUrl();
  }

  /**
   * Check if current user is SuperAdmin from SYSTEM tenant
   */
  private isSuperAdmin(): boolean {
    const userRole = localStorage.getItem('userRole');
    const tenantId = localStorage.getItem('tenantId');
    return userRole === 'SuperAdmin' && tenantId === 'SYSTEM';
  }

  /**
   * Build URL with tenant context switching support
   */
  private buildURL(endpoint: string, operatingTenantId?: string): string {
    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Add operatingTenantId parameter for SuperAdmin context switching
    if (operatingTenantId && this.isSuperAdmin()) {
      url.searchParams.append('operatingTenantId', operatingTenantId);
      tenantConfigLogger.debug('Operating as different tenant', { 
        operatingTenantId,
        endpoint 
      });
    }
    
    return url.toString();
  }

  /**
   * 🚀 FIXED: Now uses centralized authService for all requests (consistent with inventoryAPI)
   */
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {},
    operatingTenantId?: string
  ): Promise<TenantConfigAPIResponse<T>> {
    const fullURL = this.buildURL(endpoint, operatingTenantId);
    const endTimer = logger.startTimer('TenantConfigAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('TenantConfigAPI', options.method || 'GET', fullURL, options.body);

    try {
      const response = await authService.makeAuthenticatedRequest(fullURL, options);

      logger.logAPIResponse('TenantConfigAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      if (!response.ok) {
        // Log response text for debugging
        const responseText = await response.text();
        tenantConfigLogger.error('API request failed', { 
          endpoint,
          status: response.status,
          statusText: response.statusText,
          responseText: responseText.substring(0, 200) // Truncate for logging
        });
        
        return {
          success: false,
          error: {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}: ${response.statusText} - ${responseText}`
          }
        };
      }

      const data = await response.json();
      tenantConfigLogger.debug('API request successful', { 
        endpoint,
        hasData: !!data,
        dataKeys: data ? Object.keys(data) : []
      });
      
      return {
        success: true,
        data: data
      };
    } catch (error: any) {
      endTimer();
      tenantConfigLogger.error('Request failed', { endpoint, method: options.method }, error);
      
      // Handle auth service error types
      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' }
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' }
        };
      }

      if (error.code === 'NETWORK_ERROR') {
        return {
          success: false,
          error: { code: 'NETWORK_ERROR', message: error.message }
        };
      }

      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error.message || 'Unknown error occurred'
        }
      };
    }
  }

  // Get all configurations for the tenant
  async getAllConfigurations(operatingTenantId?: string): Promise<TenantConfigAPIResponse<GetAllConfigurationsResponse>> {
    tenantConfigLogger.debug('Fetching all configurations', { operatingTenantId });
    return this.makeRequest<GetAllConfigurationsResponse>('/tenant-config', {}, operatingTenantId);
  }

  // Get a specific configuration by key
  async getConfiguration(configurationKey: string, operatingTenantId?: string): Promise<TenantConfigAPIResponse<GetConfigurationResponse>> {
    tenantConfigLogger.debug('Fetching configuration', { configurationKey, operatingTenantId });
    return this.makeRequest<GetConfigurationResponse>(`/tenant-config/${configurationKey}`, {}, operatingTenantId);
  }

  // Create a new configuration
  async createConfiguration(
    configuration: CreateConfigurationRequest,
    operatingTenantId?: string
  ): Promise<TenantConfigAPIResponse<{ configuration: TenantConfiguration; message: string }>> {
    tenantConfigLogger.info('Creating configuration', { 
      configurationKey: configuration.configurationKey,
      displayName: configuration.displayName,
      valueCount: configuration.values.length,
      operatingTenantId 
    });
    
    return this.makeRequest(`/tenant-config`, {
      method: 'POST',
      body: JSON.stringify({ configuration })
    }, operatingTenantId);
  }

  // Update an existing configuration
  async updateConfiguration(
    configurationKey: string,
    configuration: UpdateConfigurationRequest,
    operatingTenantId?: string
  ): Promise<TenantConfigAPIResponse<{ configuration: TenantConfiguration; message: string }>> {
    tenantConfigLogger.info('Updating configuration', { 
      configurationKey,
      displayName: configuration.displayName,
      valueCount: configuration.values.length,
      operatingTenantId
    });
    
    return this.makeRequest(`/tenant-config/${configurationKey}`, {
      method: 'PUT',
      body: JSON.stringify({ configuration })
    }, operatingTenantId);
  }

  // Helper method to get configuration values only (for dropdown usage)
 async getConfigurationValues(configurationKey: string, operatingTenantId?: string): Promise<string[]> {
    const response = await this.getConfiguration(configurationKey, operatingTenantId);
   
    
   if (response.success && response.data?.data?.configuration?.values) {
      tenantConfigLogger.debug('Configuration values retrieved', { 
        configurationKey,
        valueCount: response.data.data.configuration.values.length 
      });
      return response.data.data.configuration.values;
    }
    
    // Return empty array if configuration doesn't exist or error occurred
    tenantConfigLogger.warn('Failed to get configuration values', { 
      configurationKey,
      error: response.error 
    });
    return [];
  }

  // Convenience methods for specific configuration types
  async getUserRanks(operatingTenantId?: string): Promise<string[]> {
    return this.getConfigurationValues('user.ranks', operatingTenantId);
  }

  async getUserAssignments(operatingTenantId?: string): Promise<string[]> {
    return this.getConfigurationValues('user.assignments', operatingTenantId);
  }

  async getUserDepartments(operatingTenantId?: string): Promise<string[]> {
    return this.getConfigurationValues('user.departments', operatingTenantId);
  }

  async getUserShifts(operatingTenantId?: string): Promise<string[]> {
    return this.getConfigurationValues('user.shifts', operatingTenantId);
  }

  // REMOVED: getUserCertifications method

  async getInventoryCategories(operatingTenantId?: string): Promise<string[]> {
    return this.getConfigurationValues('inventory.categories', operatingTenantId);
  }

  async getInventoryLocations(operatingTenantId?: string): Promise<string[]> {
    return this.getConfigurationValues('inventory.locations', operatingTenantId);
  }

  async getAssignmentReasons(operatingTenantId?: string): Promise<string[]> {
    return this.getConfigurationValues('assignment.reasons', operatingTenantId);
  }

  // Bulk operations
  async initializeDefaults(operatingTenantId?: string): Promise<TenantConfigAPIResponse<any>> {
    tenantConfigLogger.info('Initializing default configurations', { operatingTenantId });
    return this.makeRequest('/tenant-config/initialize', {
      method: 'POST'
    }, operatingTenantId);
  }

  async resetToDefault(configurationKey: string, operatingTenantId?: string): Promise<TenantConfigAPIResponse<any>> {
    tenantConfigLogger.warn('Resetting configuration to default', { configurationKey, operatingTenantId });
    return this.makeRequest(`/tenant-config/reset/${configurationKey}`, {
      method: 'POST'
    }, operatingTenantId);
  }
}

// Export singleton instance
export const tenantConfigAPI = new TenantConfigAPI();
